import { Button, Checkbox, Divider, Form, Modal, Text } from '@/components';
import { useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

type LocalStorageCategory = {
  key: string;
  label: string;
  keys: string[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const LocalStorageDeleteModal: FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const localStorageCategories: LocalStorageCategory[] = [
    {
      key: 'dev-tools',
      label: t('dev-tools.localstorage-category.dev-tools'),
      keys: ['dev-tools'],
    },
    {
      key: 'feature-flags',
      label: t('dev-tools.localstorage-category.feature-flags'),
      keys: ['feature-flags'],
    },
    {
      key: 'favorites',
      label: t('dev-tools.localstorage-category.favorites'),
      keys: ['favorite'],
    },
    {
      key: 'comments',
      label: t('dev-tools.localstorage-category.comments'),
      keys: [
        'comment-search-history',
        'comment-search-accordion-open-setting',
        'comment.filter-sider',
      ],
    },
    {
      key: 'projects',
      label: t('dev-tools.localstorage-category.projects'),
      keys: ['rs-project-search-history'],
    },
    {
      key: 'workflows',
      label: t('dev-tools.localstorage-category.workflows'),
      keys: ['workflow-search-accordion-open-setting'],
    },
  ];

  const handleCategoryChange = (categoryKey: string, checked: boolean) => {
    if (checked) {
      setSelectedCategories((prev) => [...prev, categoryKey]);
    } else {
      setSelectedCategories((prev) =>
        prev.filter((key) => key !== categoryKey),
      );
    }
  };

  const handleDeleteLocalStorage = () => {
    const keysToDelete = localStorageCategories
      .filter((category) => selectedCategories.includes(category.key))
      .flatMap((category) => category.keys);

    for (const key of keysToDelete) {
      localStorage.removeItem(key);
    }

    onClose();
    window.location.reload();
  };

  const handleSelectAll = () => {
    if (selectedCategories.length === localStorageCategories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(localStorageCategories.map((cat) => cat.key));
    }
  };

  return (
    <Modal
      title={t('dev-tools.localstorage-delete-label')}
      open={isOpen}
      centered
      onOk={handleDeleteLocalStorage}
      okText={t('delete')}
      okButtonProps={{
        danger: true,
        disabled: selectedCategories.length === 0,
      }}
      onCancel={onClose}
      destroyOnClose
    >
      <Text
        variant="footnote"
        weight="regular"
        color="secondary"
        text={t('dev-tools.localstorage-delete-description')}
      />

      <Divider />

      <Form
        leavePageAlertCondition={null}
        layout="vertical"
        style={{ maxHeight: 400, overflowY: 'scroll' }}
      >
        <div style={{ marginBottom: 16 }}>
          <Button
            type="link"
            onClick={handleSelectAll}
            style={{ padding: 0, height: 'auto' }}
          >
            {selectedCategories.length === localStorageCategories.length
              ? '全て解除'
              : '全て選択'}
          </Button>
        </div>

        {localStorageCategories.map((category) => (
          <div key={category.key} style={{ marginBottom: 8 }}>
            <Checkbox
              checked={selectedCategories.includes(category.key)}
              onChange={(e) =>
                handleCategoryChange(category.key, e.target.checked)
              }
            >
              {category.label}
            </Checkbox>
            <div style={{ marginLeft: 24, marginTop: 4 }}>
              <Text
                variant="footnote"
                color="secondary"
                text={`Keys: ${category.keys.join(', ')}`}
              />
            </div>
          </div>
        ))}
      </Form>
    </Modal>
  );
};
